// src/components/CourseView.js
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Avatar,
  CircularProgress,
  Fade,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Logout,
  PlayCircle,
  CheckCircle,
  School,
  Person,
  AccessTime,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { ref, get, child } from 'firebase/database';
import { database, auth } from '../firebase/Config';

const CourseView = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUserEmail(user.email);
    }
  }, []);

  const fetchCourseData = async () => {
    setLoading(true);
    try {
      const dbRef = ref(database);
      
      // Buscar dados do curso
      const courseSnapshot = await get(child(dbRef, `courses/${courseId}`));
      if (!courseSnapshot.exists()) {
        navigate('/learning');
        return;
      }
      
      setCourse({ id: courseId, ...courseSnapshot.val() });

      // Buscar aulas do curso
      const lessonsSnapshot = await get(child(dbRef, 'lessons'));
      if (lessonsSnapshot.exists()) {
        const lessonsData = lessonsSnapshot.val();
        const lessonsArray = Object.keys(lessonsData)
          .map((key) => ({
            id: key,
            ...lessonsData[key],
          }))
          .filter(lesson => lesson.courseId === courseId)
          .sort((a, b) => a.order - b.order);
        
        setLessons(lessonsArray);
        
        // Selecionar a primeira aula automaticamente
        if (lessonsArray.length > 0) {
          setSelectedLesson(lessonsArray[0]);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar dados do curso:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  const getEmbedUrl = (videoUrl) => {
    if (!videoUrl) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = videoUrl.match(regExp);
    const videoId = (match && match[2].length === 11) ? match[2] : null;
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} sx={{ color: '#48bb78' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(90deg, #48bb78 0%, #38a169 100%)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <IconButton
            onClick={() => navigate('/learning')}
            sx={{
              color: '#fff',
              mr: 2,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <ArrowBack />
          </IconButton>
          <School sx={{ mr: 2, fontSize: 32 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                letterSpacing: '0.5px',
              }}
            >
              {course?.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {lessons.length} {lessons.length === 1 ? 'aula' : 'aulas'}
            </Typography>
          </Box>
          {userEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                avatar={<Avatar sx={{ bgcolor: '#fff', color: '#48bb78' }}>{userEmail.charAt(0).toUpperCase()}</Avatar>}
                label={userEmail}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  fontWeight: 500,
                  px: 1,
                }}
              />
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Logout />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        <Fade in timeout={600}>
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Video Player e Descrição */}
            <Box sx={{ flex: 1 }}>
              {selectedLesson ? (
                <>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      overflow: 'hidden',
                      mb: 3,
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        paddingTop: '56.25%', // 16:9 aspect ratio
                        bgcolor: '#000',
                      }}
                    >
                      {getEmbedUrl(selectedLesson.videoUrl) ? (
                        <iframe
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 0,
                          }}
                          src={getEmbedUrl(selectedLesson.videoUrl)}
                          title={selectedLesson.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#2d3748',
                          }}
                        >
                          <Typography variant="h6" sx={{ color: '#fff' }}>
                            Vídeo não disponível
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Card>

                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: '#2d3748',
                          mb: 2,
                        }}
                      >
                        {selectedLesson.title}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <Chip
                          icon={<PlayCircle />}
                          label={`Aula ${selectedLesson.order}`}
                          size="small"
                          sx={{
                            bgcolor: '#48bb78',
                            color: '#fff',
                            fontWeight: 600,
                          }}
                        />
                        {selectedLesson.duration && (
                          <Chip
                            icon={<AccessTime />}
                            label={selectedLesson.duration}
                            size="small"
                            sx={{
                              bgcolor: '#f7fafc',
                              color: '#718096',
                            }}
                          />
                        )}
                      </Box>

                      <Divider sx={{ mb: 2 }} />

                      <Typography
                        variant="body1"
                        sx={{
                          color: '#718096',
                          lineHeight: 1.8,
                        }}
                      >
                        {selectedLesson.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    p: 6,
                    textAlign: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                  }}
                >
                  <PlayCircle sx={{ fontSize: 80, color: '#cbd5e0', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#718096' }}>
                    Selecione uma aula para assistir
                  </Typography>
                </Card>
              )}
            </Box>

            {/* Lista de Aulas */}
            <Box sx={{ width: { xs: '100%', md: 380 } }}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  maxHeight: 'calc(100vh - 150px)',
                  overflow: 'auto',
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: 3, bgcolor: '#f7fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#2d3748',
                        mb: 1,
                      }}
                    >
                      Conteúdo do Curso
                    </Typography>
                    {course && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Person sx={{ fontSize: 16, color: '#718096' }} />
                          <Typography variant="body2" sx={{ color: '#718096' }}>
                            {course.instructor}
                          </Typography>
                        </Box>
                        {course.duration && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTime sx={{ fontSize: 16, color: '#718096' }} />
                            <Typography variant="body2" sx={{ color: '#718096' }}>
                              {course.duration}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>

                  <List sx={{ p: 0 }}>
                    {lessons.map((lesson, index) => (
                      <React.Fragment key={lesson.id}>
                        <ListItem disablePadding>
                          <ListItemButton
                            onClick={() => setSelectedLesson(lesson)}
                            selected={selectedLesson?.id === lesson.id}
                            sx={{
                              py: 2,
                              px: 3,
                              '&.Mui-selected': {
                                bgcolor: '#48bb7810',
                                borderLeft: '4px solid #48bb78',
                                '&:hover': {
                                  bgcolor: '#48bb7820',
                                },
                              },
                            }}
                          >
                            <ListItemIcon>
                              {selectedLesson?.id === lesson.id ? (
                                <CheckCircle sx={{ color: '#48bb78' }} />
                              ) : (
                                <PlayCircle sx={{ color: '#718096' }} />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: selectedLesson?.id === lesson.id ? 700 : 600,
                                    color: selectedLesson?.id === lesson.id ? '#48bb78' : '#2d3748',
                                  }}
                                >
                                  {lesson.order}. {lesson.title}
                                </Typography>
                              }
                              secondary={
                                lesson.duration && (
                                  <Typography variant="caption" sx={{ color: '#718096' }}>
                                    {lesson.duration}
                                  </Typography>
                                )
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                        {index < lessons.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>

                  {lessons.length === 0 && (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                      <PlayCircle sx={{ fontSize: 60, color: '#cbd5e0', mb: 2 }} />
                      <Typography variant="body2" sx={{ color: '#718096' }}>
                        Nenhuma aula disponível ainda
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default CourseView;
